import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Course, Instructions, Sessions, User } from '@app/models/_index';
import { CardTypeCallToActions, CardTypeSlugs, InstructionsSteps } from '@app/enums/_index';
import { EnrollmentService, GoogleAnalyticsService, UserService } from '@app/services/_index';
import { hasSubscription, logGoogleAnalytics } from '@app/utilities/_index';
import { ModalDialogComponent } from '@app/components/_index';
import { Router } from '@angular/router';
import { InstructionsItemFactory } from '@app/factories/_index';

@Component({
  selector: 'app-sessions',
  templateUrl: './sessions.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./sessions.component.scss'],
  standalone: false,
})
export class SessionsComponent implements OnInit {
  @Input() title: string; // Toggle to change background styling from light or dark
  @Input() sessions: Sessions[] = [];
  @Input() course: Course;
  @Input() currentUser: User;

  @Output() sendClick = new EventEmitter<boolean>();

  // Related to Show more
  startPage = 0;
  paginationLimit = 3;

  courseId: number;
  slug: string;
  sessionId: number;
  sessionName: string;

  // Instructions
  instructions: Instructions[] = [];

  // Used for Session enrollment error message in upcoming sessions
  sessionEnrollErrorMessage: {};

  @ViewChild('modalDialog') modalDialog: ModalDialogComponent;

  constructor(
    private router: Router,
    private googleAnalyticsService: GoogleAnalyticsService,
    private userService: UserService,
    private enrollmentService: EnrollmentService,
    private instructionsItemFactory: InstructionsItemFactory
  ) {}

  ngOnInit() {
    this.instructions = this.instructionsItemFactory.getCollection();
  }
  /**
   * Show more items for Upcoming Sessions.
   */
  showMoreItems(): void {
    this.paginationLimit = Number(this.paginationLimit) + 3;
  }

  /**
   * Add Track By Course Session item to not redraw or
   * re-render list items that have not changed. This improves performance.
   * @param index - Index of Course Session array
   * @param item - Course Session list item
   * @returns Session id
   */
  trackByCourseSessionsId(index: number, item: Sessions): number {
    if (item) {
      return item.idSession;
    } else {
      return index;
    }
  }

  /**
   * Open Enroll Session Modal if user successfully enroll into a course session
   * for Instructor-Led courses.
   * This modal is custom since it needs to show to the user the steps
   * to confirm their successful enrollment.
   * @param isClicked - handle event emited from couse session card
   * @param courseId - courseId for the given course session to be enrolled
   * @param sessionId - sessionId needed to be enrolled for a given course id
   */
  openEnrollSessionModal(isClicked: boolean, courseId: number, sessionId: number): void {
    if (isClicked) {
      this.loadEnrollSession(courseId, sessionId);
    }
  }

  /**
   * Open Unenroll Session Modal if user successfully unenroll into a course session
   * for Instructor-Led courses.
   * This modal is custom since it needs to show to the user the steps
   * to confirm their successful enrollment.
   * @param isClicked - handle event emited from couse session card
   * @param courseId - courseId for the given course session to be enrolled
   * @param sessionId - sessionId needed to be enrolled for a given course id
   */
  openUnenrollSessionModal(isClicked: boolean, courseId: number, session: any) {
    if (isClicked) {
      this.courseId = courseId;
      this.sessionId = session.idSession;
      this.sessionName = session.name;
      this.modalDialog.openModal();
    }
  }

  /**
   * Check the EnrollSession API to see and if the response is successful load the enrolledContent popup or throw an error
   * @param courseId pass the courseId
   * @param sessionId pass the sessionId
   */
  loadEnrollSession(courseId: number, sessionId: number): void {
    if (this.currentUser) {
      const canEnrollIntoSession = hasSubscription(
        this.currentUser,
        this.course.subscriptionId,
        this.course.relatedSubscriptionIds
      );
      const sessions = this.course?.sessions?.filter((session) => session.idSession == sessionId);
      if (canEnrollIntoSession) {
        if (sessions.length > 0) {
          this.enrollmentService
            .enrollSession(this.currentUser.id, courseId, sessionId)
            .then((response: any) => {
              if (response && response.enrolled && response.enrolled.length > 0) {
                // If the response is successful show the enrolledContent modal.
                if (document.getElementById('enrolledContent')) {
                  document.getElementById('enrolledContent').style.display = 'block';
                }
              } else {
                // Show enrollment errors in UI if any
                const errorObj = { idSession: sessionId, errorMessage: response.errors[0] };
                this.sessionEnrollErrorMessage = errorObj;
              }
            })
            .catch((error) => {
              console.error('Load enroll session error', error);
            });
        }
      } else {
        this.router.navigate([`subscription/${this.course.subscriptionSlugName}`]);
      }
    }
  }

  /**
   * Unenroll the session
   */
  unEnrollSession() {
    this.enrollmentService
      .unEnrollSession(this.currentUser.id, this.courseId, this.sessionId)
      .then((response: any) => {
        if (response && response.data && response.data.success == true) {
          this.onClick(true);
        } else {
          // Show unenrollment errors in UI if any
          const errorObj = { idSession: this.sessionId, errorMessage: response.data.errors[0] };
          this.sessionEnrollErrorMessage = errorObj;
        }
      })
      .catch((error) => {
        const errorObj = { idSession: this.sessionId, errorMessage: error.message };
        this.sessionEnrollErrorMessage = errorObj;
      });
  }

  /**
   * OnClick handles the flag and reloads the current
   * user's enrollment states to pick up new enrollments
   * @param clickButtonFlag returns boolean flag true
   */
  onClick(clickButtonFlag: boolean): void {
    if (clickButtonFlag) {
      //this.loadCourse(this.courseId, this.slug);
      this.sendClick.emit(true);
      // NOTE: Must pass in the current user to update the current
      // users cached enrollments to determine the sessions enrolled
      // which in turn updates the pipes check for changes in user.
      this.userService.loadEnrollments(this.currentUser);
    }
  }

  /**
   * Return an Instructions item by instruction name
   * @param name - instructions name
   * @returns - Instruction model
   */
  getInstructionsByName(name: string): string[] {
    let steps: string[] = [];
    const instructions = this.instructionsItemFactory.getCollectionByInstructionName(name);
    if (instructions) {
      steps = instructions.steps;
    }
    return steps;
  }

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: any): void {
    const actionLabel = dataLabel && dataLabel.actionLabel ? dataLabel.actionLabel : dataLabel;
    logGoogleAnalytics(category, action, actionLabel);
    this.googleAnalyticsService.sendEvent(category, action, actionLabel);
  }

  /**
   * Convert to a integer value
   * @param val to convert to Integer
   * @returns an integer value
   */
  getIntegerValue(val: number): number {
    let intVal;
    try {
      intVal = parseInt(String(val), 10);
    } catch (e) {}

    if (Number.isInteger(intVal)) {
      return intVal;
    }

    return NaN;
  }

  /**
   *  CardTypeSlugs enums based on card types
   */
  public get cardTypeSlugs(): typeof CardTypeSlugs {
    return CardTypeSlugs;
  }

  /**
   *  CardTypeCallToActions enums based on Action
   */
  public get cardTypeCallToActions(): typeof CardTypeCallToActions {
    return CardTypeCallToActions;
  }

  /**
   *  InstructionsSteps enums based on instruction steps
   */
  public get instructionSteps(): typeof InstructionsSteps {
    return InstructionsSteps;
  }
}
